import React from 'react';
import type { FC, ReactNode } from 'react';
import { styled } from '@compiled/react';
import { Subscribe } from 'unstated';

import { token } from '@atlaskit/tokens';

import { EditorContentState } from '@confluence/content-state/entry-points/EditorContentState.ts';
import { ContentStateStateContainer } from '@confluence/content-state/entry-points/ContentStateStateContainer.ts';
import { type FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { TwoStageHydration } from '@confluence/ssr-utilities/entry-points/TwoStageHydration.ts';

import { LivePagesByLine } from './LivePagesByLine.tsx';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EditorByLineWrapper = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Spacer = styled.div({
	flexGrow: 1,
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RightArea = styled.div({
	display: 'flex',
	minHeight: token('space.400'),
	alignItems: 'center',
});

export type EditorByLineAreaProps = {
	contentId: string;
	contentType: string;
	isPublishingContent: boolean;
	userOffline: boolean;
	editorAppearanceButton: ReactNode;
	isLivePage: boolean;
	isKnowledgeBaseSpace: boolean;
	viewMode?: boolean;
	editorApi?: FullPageEditorPresetAPI | null;
	isUnpublishedDraft?: boolean;
	isSpaceOverview?: boolean;
	hasByLineContributors: boolean;
	hasByLineExtensions: boolean;
	hasReactions: boolean;
};

export const EditorByLineArea: FC<EditorByLineAreaProps> = ({
	contentId,
	contentType,
	isPublishingContent,
	userOffline,
	editorAppearanceButton,
	isLivePage,
	isKnowledgeBaseSpace,
	viewMode,
	editorApi,
	isUnpublishedDraft,
	isSpaceOverview,
	hasByLineContributors,
	hasByLineExtensions,
	hasReactions,
}) => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();

	if (isSpaceOverview) {
		return null;
	}

	if (isLivePage || fg('cc_page_experiences_new_editor_byline')) {
		return (
			<TwoStageHydration
				name="editor-byline"
				enabled={expValEquals('platform_editor_hydratable_ui', 'isEnabled', true)}
			>
				<EditorByLineWrapper data-vc="editor-byline-wrapper" {...ssrPlaceholderIdProp}>
					<LivePagesByLine
						contentId={contentId}
						contentType={contentType}
						isPublishingContent={isPublishingContent}
						userOffline={userOffline}
						isKnowledgeBaseSpace={isKnowledgeBaseSpace}
						viewMode={viewMode}
						editorApi={editorApi}
						isUnpublishedDraft={isUnpublishedDraft}
						hasByLineContributors={hasByLineContributors}
						hasByLineExtensions={hasByLineExtensions}
						hasReactions={hasReactions}
					/>
				</EditorByLineWrapper>
			</TwoStageHydration>
		);
	}

	return (
		<TwoStageHydration
			name="editor-byline"
			enabled={expValEquals('platform_editor_hydratable_ui', 'isEnabled', true)}
		>
			<EditorByLineWrapper data-vc="editor-byline-wrapper" {...ssrPlaceholderIdProp}>
				<>
					<Spacer />
					<RightArea data-testId="editor-byline-right-area">
						<Subscribe to={[ContentStateStateContainer]}>
							{(contentStateState: ContentStateStateContainer) => {
								// Knowledge space pages without the new byline will always show the editable content state component in this area.
								// Other pages will only show the editable content state component if the content state is already set.
								// This is because a page without a content state is initialized by the ContentStateHoverButton component.
								return (
									(contentStateState.state.contentState ||
										isKnowledgeBaseSpace ||
										expValEquals('confluence_approvals_eap', 'isEnabled', true)) && (
										<EditorContentState
											contentId={contentId}
											contentType={contentType}
											isPublishingContent={isPublishingContent}
											userOffline={userOffline}
										/>
									)
								);
							}}
						</Subscribe>
						{editorAppearanceButton}
					</RightArea>
				</>
			</EditorByLineWrapper>
		</TwoStageHydration>
	);
};
